import { useManager } from 'dreamstate';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';

import {
  Box,
  Flex,
  rem,
  SystemStyleObject,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  TabIndicator,
} from '@vimeo/bokeh';
import { EditPencil, TrashBin } from '@vimeo/bokeh-icons';
import { createInteractionDomName } from '@vimeo/cloud-live/libs/shared/utils/dom/identifying';
import { translations } from '@vimeo/cloud-live/translations';
import { Maybe, Optional } from '@vimeo/core/types/generic';
import { PollForm } from '@vimeo/live-core/components/interaction/polls/poll_form/PollForm';
import { PollItem } from '@vimeo/live-core/components/interaction/polls/poll_item/PollItem';
import { usePollPanels } from '@vimeo/live-core/components/interaction/polls/poll_panels/usePollPanels';
import { PollEmptyState } from '@vimeo/live-core/components/interaction/polls/PollEmptyState';
import { BlockingLoadingWrapper } from '@vimeo/live-core/components/loading';
import { CreateButtonMinimized } from '@vimeo/live-core/components/panel';
import {
  createTabListScrollStyle,
  createTabListColumnStyle,
  TAB_LIST_COLUMN_ROW_ITEM_STYLE,
  TABS_STYLES,
  TAB_PANELS_STYLES,
  TAB_PANEL_STYLES,
} from '@vimeo/live-core/components/panels/styles';
import { useLiveGlobals } from '@vimeo/live-core/components/providers/globals';
import { HorizontalScrollShadow } from '@vimeo/live-core/components/scroll_shadow';
import { useResponsiveSidebarContext } from '@vimeo/live-core/components/sidebar';
import { useScrollbarStyles } from '@vimeo/live-core/hooks/styling/useScrollbarStyles';
import { PollManager } from '@vimeo/live-core/store/firebase/interaction/poll';
import {
  trackClickAddPoll,
  trackClickEditPoll,
  trackRemovePollFromEvent,
  trackViewPoll,
} from '@vimeo/live-core/tracking/tracking_engagement_polls';
import { trackVenuesViewPoll } from '@vimeo/live-core/tracking/tracking_venues_engagement';
import { T_CREATE_POLL } from '@vimeo/live-core/translations';
import { EComposerSessionType } from '@vimeo/live-core/types/composer_session';
import {
  EPollState,
  IPoll,
  IPollItemMenuActionItem,
} from '@vimeo/live-core/types/interaction_tools';
import { IInteractionStoreInitialState } from '@vimeo/live-core/types/store';
import {
  createPollInteractionObject,
  createPollOptionInteractionObject,
} from '@vimeo/live-core/utils/firebase/interaction_objects';

const POLL_MANAGE_WRAPPER_STYLE: SystemStyleObject = {
  position: 'relative',
  flexGrow: 1,
  flexDirection: 'column',
  height: '100%',
  overflow: 'hidden',
  padding: rem(24),
};

function createPollFormWrapperStyle(): SystemStyleObject {
  return {
    padding: `${rem(16)} ${rem(8)}`,
    height: '100%',
    overflowY: 'auto',
    overflowX: 'visible',
    // Fix for firefox due to it does not support scrollbar styling
    '@supports (-moz-appearance: none)': {
      paddingRight: rem(20),
    },
  };
}

const defaultPoll: IPoll = createPollInteractionObject(
  {
    id: '',
    question: '',
    state: EPollState.DRAFT,
    createdAt: 0,
    options: [
      createPollOptionInteractionObject(
        {
          id: uuid(),
          text: '',
        },
        { validate: false },
      ),
      createPollOptionInteractionObject(
        {
          id: uuid(),
          text: '',
        },
        { validate: false },
      ),
    ],
  },
  { validate: false },
);

function getPollItemMenuActions(
  poll: IPoll,
  actions: { onPollEdit(poll: IPoll): void; onPollDelete(pollId: string): void },
): Array<IPollItemMenuActionItem> {
  const canBeEdited: boolean = poll.state === EPollState.DRAFT;

  return canBeEdited
    ? [
        {
          key: 'edit',
          label: translations.edit,
          onClick: () => {
            actions.onPollEdit(poll);
            trackClickEditPoll();
          },
          icon: <EditPencil />,
        },
        {
          key: 'delete',
          label: translations.remove,
          onClick: () => {
            actions.onPollDelete(poll.id);
            trackRemovePollFromEvent();
          },
          icon: <TrashBin />,
        },
      ]
    : [];
}

export function PollManage({
  id = createInteractionDomName('poll-manage'),
  className = createInteractionDomName('poll-manage'),
  pollContext: {
    isHydrated,
    activePollId,
    polls,
    pollsHistory,
    pollActions: { createPoll, updatePoll, deletePoll, setActivePoll },
    // eslint-disable-next-line react-hooks/rules-of-hooks
  } = useManager(PollManager),
}) {
  const [isCreatePollFormOpened, setIsCreateFormOpened] = useState<boolean>(false);
  const [pollForEdit, setPollForEdit] = useState<Optional<IPoll>>(null);
  const {
    initialState,
    initialState: { sessionType },
  } = useLiveGlobals<IInteractionStoreInitialState>();
  const { setIsHeaderEnabled } = useResponsiveSidebarContext();

  const allPolls: Array<IPoll> = useMemo(() => Object.values(polls), [polls]);
  const isAnyPollOpen: boolean = allPolls.some((poll: IPoll) => poll.state === EPollState.OPENED);
  const isAnyPollPublished: boolean = allPolls.some(
    (poll: IPoll) => poll.state === EPollState.CLOSED,
  );

  const canOpenPoll: boolean = !isAnyPollOpen;

  const { panels, activePolls, activePanelId, setActivePanelId } = usePollPanels(polls);

  const onCreateClick = useCallback(() => {
    setIsHeaderEnabled(false);
    setIsCreateFormOpened(true);
    trackClickAddPoll();
  }, [setIsHeaderEnabled]);

  const onPollClose = useCallback(
    (poll: IPoll) => {
      updatePoll(poll.id, { ...poll, state: EPollState.CLOSED });
    },
    [updatePoll],
  );

  const onPollArchive = useCallback(
    (poll: IPoll) => {
      updatePoll(poll.id, { ...poll, state: EPollState.ARCHIVED });
    },
    [updatePoll],
  );

  const onPollOpen = useCallback(
    (poll: IPoll) => {
      const activePoll: Maybe<IPoll> = activePollId ? polls[activePollId] : null;

      if (activePoll) {
        onPollArchive(activePoll);
      }

      updatePoll(poll.id, { ...poll, state: EPollState.OPENED });
      setActivePoll(poll.id);
    },
    [activePollId, polls, updatePoll, setActivePoll, onPollArchive],
  );

  const onPollEdit = useCallback(
    (poll: IPoll) => {
      setIsHeaderEnabled(false);
      setPollForEdit(poll);
    },
    [setIsHeaderEnabled],
  );

  const onPollDelete = useCallback(
    (pollId: string) => {
      deletePoll(pollId);
    },
    [deletePoll],
  );

  const onCreatePollFormClose = useCallback(() => {
    setIsCreateFormOpened(false);
    setIsHeaderEnabled(true);
  }, [setIsHeaderEnabled]);

  const onCreateFormSubmit = useCallback(
    (poll: IPoll) => {
      setIsCreateFormOpened(false);
      setIsHeaderEnabled(true);
      return createPoll(poll);
    },
    [createPoll, setIsHeaderEnabled],
  );

  const onEditPollFormClose = useCallback(() => {
    setPollForEdit(null);
    setIsHeaderEnabled(true);
  }, [setIsHeaderEnabled]);

  const onEditFormSubmit = useCallback(
    (poll: IPoll) => {
      setPollForEdit(null);
      setIsHeaderEnabled(true);
      return updatePoll(poll.id, poll);
    },
    [setIsHeaderEnabled, updatePoll],
  );

  useEffect(() => {
    trackViewPoll();
  }, []);

  useEffect(() => {
    if (isHydrated && activePollId && sessionType === EComposerSessionType.VENUE) {
      trackVenuesViewPoll(String(activePollId));
    }
  }, [activePollId, isHydrated, sessionType]);

  const scrollbarStyles: SystemStyleObject = useScrollbarStyles();

  if (isCreatePollFormOpened) {
    return (
      <Box id={id} className={className} sx={createPollFormWrapperStyle()}>
        <PollForm
          poll={defaultPoll}
          onClose={onCreatePollFormClose}
          onSubmit={onCreateFormSubmit}
        />
      </Box>
    );
  }

  if (pollForEdit) {
    return (
      <Box id={id} className={className} sx={createPollFormWrapperStyle()}>
        <PollForm poll={pollForEdit} onClose={onEditPollFormClose} onSubmit={onEditFormSubmit} />
      </Box>
    );
  }

  if (!isHydrated) {
    return (
      <Flex id={id} className={className} sx={POLL_MANAGE_WRAPPER_STYLE}>
        <BlockingLoadingWrapper
          id={createInteractionDomName(id, 'loader')}
          opacity={0.1}
          withAnimation={true}
        />
      </Flex>
    );
  }

  if (!allPolls.length) {
    return (
      <PollEmptyState
        id={id}
        className={className}
        buttonLabel={T_CREATE_POLL}
        description={translations.createPollToInteractWithAudience}
        history={pollsHistory}
        maxWidth={rem(360)}
        sx={POLL_MANAGE_WRAPPER_STYLE}
        onCreateClick={onCreateClick}
      />
    );
  }

  return (
    <Flex id={id} className={className} sx={POLL_MANAGE_WRAPPER_STYLE}>
      <Tabs size={'sm'} sx={TABS_STYLES} onChange={setActivePanelId}>
        <TabList>
          {panels.map(({ id: panelId, label }) => (
            <Tab key={panelId} className={createInteractionDomName(id, panelId, 'tab')}>
              {label}
            </Tab>
          ))}

          <TabIndicator />
        </TabList>

        <TabPanels key={activePanelId} sx={TAB_PANELS_STYLES}>
          {panels.map(({ id: panelId, polls }) => (
            <TabPanel
              key={panelId}
              className={createInteractionDomName(id, panelId, 'tab-panel')}
              sx={TAB_PANEL_STYLES}
            >
              <Box
                id={createInteractionDomName(id, panelId, 'scroll')}
                sx={{
                  ...createTabListScrollStyle({ fullWidth: true }),
                  ...scrollbarStyles,
                }}
              >
                <Box
                  id={createInteractionDomName(id, panelId, 'column')}
                  sx={createTabListColumnStyle(true)}
                >
                  {polls.length === 0 ? (
                    <PollEmptyState
                      id={createInteractionDomName(id, panelId, 'empty-placeholder')}
                      className={createInteractionDomName(id, panelId, 'empty-placeholder')}
                      width={'100%'}
                      height={'100%'}
                      buttonLabel={T_CREATE_POLL}
                      description={
                        panelId === 0
                          ? translations.createPollToInteractWithAudience
                          : translations.noPollsArchived
                      }
                      control={panelId === 0}
                      history={panelId === 0 ? pollsHistory : null}
                      onCreateClick={onCreateClick}
                    />
                  ) : (
                    polls.map((it) => {
                      return (
                        <Box key={it.id} sx={TAB_LIST_COLUMN_ROW_ITEM_STYLE}>
                          <PollItem
                            poll={it}
                            isAnyPollPublished={isAnyPollPublished}
                            canOpenPoll={canOpenPoll}
                            menuAttach={'left-start'}
                            itemActions={{
                              onPollOpen,
                              onPollClose,
                              onPollArchive,
                            }}
                            menuActions={getPollItemMenuActions(it, { onPollEdit, onPollDelete })}
                          />
                        </Box>
                      );
                    })
                  )}
                </Box>
              </Box>
            </TabPanel>
          ))}
        </TabPanels>
      </Tabs>

      <Flex position={'relative'}>
        <HorizontalScrollShadow
          color={initialState?.interaction?.backgroundColor || 'background'}
        />
      </Flex>

      {activePanelId === 0 && activePolls.length !== 0 ? (
        <CreateButtonMinimized
          id={createInteractionDomName(id, 'add-button')}
          onClick={onCreateClick}
        >
          {translations.addPoll}
        </CreateButtonMinimized>
      ) : null}
    </Flex>
  );
}
